/**
 * Computes the breakdown / column-key parts of a dataset instance's config
 * from the deliverable's API definition.
 *
 * Called from the toggle/add paths only:
 *   - Step 1's `toggleDatasetType` (new selection)
 *   - Step 2's dataset switcher (`addDatasetInstance`, e.g. duplicate)
 *
 * Hydration (`hydrateStoreFromPayload`) does NOT call this — the saved payload
 * already lists the included files explicitly, so hydration just copies that
 * list into `includedBreakdownIds` directly.
 */
import type { ConditionContext } from '../api/evaluate-condition';
import type { DatasetInstanceConfig } from '../data-builder.store';
import {
  AGGREGATION_BREAKDOWN_KEY,
  getVisibleColumnKeysByBreakdown,
  isConditionVisible,
} from '../steps/step2-column/filter-config-by-models';
import type { DeliverableColumnConfig } from './deliverable-column-config';

type ColumnOptionLike = {
  key: string;
  isDefault: boolean;
  isRequired: boolean;
};

export const collectDefaultColumnKeys = (
  options: ColumnOptionLike[]
): string[] => {
  const seen = new Set<string>();
  const keys: string[] = [];
  for (const o of options) {
    if ((o.isDefault || o.isRequired) && !seen.has(o.key)) {
      seen.add(o.key);
      keys.push(o.key);
    }
  }
  return keys;
};

export type SeededInstanceConfig = Pick<
  DatasetInstanceConfig,
  'columnKeysByBreakdown' | 'includedBreakdownIds'
>;

/**
 * Derive the parts of a fresh instance config that depend on the deliverable's
 * schema:
 *   - `includedBreakdownIds` — the schema's default-flagged breakdowns
 *   - `columnKeysByBreakdown` — default + required keys for the aggregation
 *     and each included breakdown
 *
 * When a condition `context` is supplied (the instance's initial settings +
 * model versions), the seed is restricted to what is actually *visible* for
 * that context, reusing the same {@link getVisibleColumnKeysByBreakdown} the
 * Step 2 reconciliation applies on setting changes. This keeps the seed and
 * change paths consistent for every condition level — column, group,
 * aggregation-section and breakdown/file — so a condition-gated default is
 * never seeded when its condition can't hold (e.g. the unified-only
 * `expected_hires` for a client without unified posting-source access, which
 * would otherwise leak into the first `/estimate`).
 */
export const seedInstanceDefaults = (
  deliverableConfig: DeliverableColumnConfig,
  context?: ConditionContext
): SeededInstanceConfig => {
  const visibleColumnKeysByBreakdown =
    context === undefined
      ? undefined
      : getVisibleColumnKeysByBreakdown(deliverableConfig, context);

  const keepVisible = (breakdownId: string, keys: string[]): string[] => {
    if (visibleColumnKeysByBreakdown === undefined) {
      return keys;
    }
    const visible = new Set(visibleColumnKeysByBreakdown[breakdownId] ?? []);
    return keys.filter((key) => visible.has(key));
  };

  const includedBreakdownIds = deliverableConfig.breakdowns
    .filter(
      (breakdown) =>
        breakdown.isDefault &&
        (context === undefined ||
          isConditionVisible(breakdown.condition, context))
    )
    .map((breakdown) => breakdown.id);

  const columnKeysByBreakdown: Record<string, string[]> = {};

  const aggregationKeys = keepVisible(
    AGGREGATION_BREAKDOWN_KEY,
    collectDefaultColumnKeys(deliverableConfig.aggregationColumns.options)
  );
  if (aggregationKeys.length > 0) {
    columnKeysByBreakdown[AGGREGATION_BREAKDOWN_KEY] = aggregationKeys;
  }

  const includedSet = new Set(includedBreakdownIds);
  for (const breakdown of deliverableConfig.breakdowns) {
    if (!includedSet.has(breakdown.id)) continue;
    const keys = keepVisible(
      breakdown.id,
      collectDefaultColumnKeys(breakdown.columnOptions)
    );
    if (keys.length > 0) {
      columnKeysByBreakdown[breakdown.id] = keys;
    }
  }

  return {
    includedBreakdownIds,
    columnKeysByBreakdown,
  };
};
